import { MouseEvent, useMemo, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  IconCirclePlus,
  IconCustomMenuDown,
  IconX,
} from '@rossum/ui/icons/tabler';
import {
  Button,
  ButtonProps,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { GridFilterItem } from '@rossum/ui/x-data-grid-pro';
import { userSelector } from '../../../redux/modules/user/selectors';
import { FilterColDef } from '../../document-list-filtering/types';
import { metaColumnsCodecTypesMap } from '../../mql/constants';
import { pickFieldsWithCodecTypes } from '../../mql/helpers';
import { isSavedViewStale, NormalizedSavedView, SystemView } from '../core';
import { useGetSavedViews } from '../hooks';
import { EmptyViewsList } from './EmptyViewsList';
import { SavedViewMenuItem } from './SavedViewMenuItem';
import {
  SavedViewDrawerState,
  SavedViewsConfigDrawer,
} from './SavedViewsConfigDrawer';

export const isKeyofMetaColumns = (
  value: string
): value is keyof typeof metaColumnsCodecTypesMap =>
  value in metaColumnsCodecTypesMap;

const matchesSearch = (name: string, search: string) =>
  name.toLowerCase().includes(search.trim().toLowerCase());

type OnMenuItemClickParams = { filterItems: GridFilterItem[]; viewId: string };

type SavedViewsListProps = {
  columns: FilterColDef[];
  currentViewConfig: {
    filtering: GridFilterItem[];
    viewId: string | undefined;
  };
  onMenuItemClick: (params: OnMenuItemClickParams) => void;
  onMenuItemDelete: (viewId: string) => void;
  onClearSelection: () => void;
  activeQueueId: number;
  buttonProps?: ButtonProps & { buttonLabel?: string };
  systemView?: SystemView;
};

export const SavedViewsList = ({
  columns,
  currentViewConfig,
  onMenuItemClick,
  onMenuItemDelete,
  onClearSelection,
  activeQueueId,
  buttonProps: { buttonLabel, ...restOfbuttonProps } = {},
  systemView,
}: SavedViewsListProps) => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [drawerState, setDrawerState] = useState<SavedViewDrawerState | null>(
    null
  );

  const [search, setSearch] = useState('');

  const currentUserUrl = useSelector(userSelector).url;

  const codecFieldTypes = useMemo(
    () =>
      R.pipe(
        columns,
        R.map(c => ({
          field: c.field,
          codecType: isKeyofMetaColumns(c.field)
            ? (metaColumnsCodecTypesMap[c.field] ?? undefined)
            : undefined,
        })),
        R.mapToObj(c => [c.field, c.codecType]),
        pickFieldsWithCodecTypes
      ),
    [columns]
  );

  const { data: savedViews, isFetching } = useGetSavedViews({
    codecFieldTypes,
    queueId: activeQueueId,
  });

  const normalizedSavedViews = useMemo(() => {
    if (!savedViews) return [];
    const currentColumnsMap = R.mapToObj(columns, c => [c.field, true]);
    return R.pipe(
      savedViews,
      R.filter(view => {
        const isAnotherUsersPersonalView =
          !view.shared && view.createdBy !== currentUserUrl;

        // per BE requirements admins can access all views (e.g. annotators views that are not shared)
        // we're filtering them out for better UX.
        return !isAnotherUsersPersonalView;
      }),
      R.map(view => {
        const missingColumns = view.parsedQuery
          .map(item => item.field)
          .filter(field => !currentColumnsMap[field]);
        return { ...view, missingColumns };
      }),
      R.filter(view => matchesSearch(view.name, search))
    );
  }, [columns, savedViews, search, currentUserUrl]);

  const isSystemViewListed =
    !!systemView && matchesSearch(systemView.label, search);

  const selectedViewId = currentViewConfig.viewId;

  const currentView = useMemo(() => {
    if (!savedViews || !selectedViewId) return undefined;
    return savedViews.find(view => view.id === Number(selectedViewId));
  }, [savedViews, selectedViewId]);

  // The selection lives in the URL, so it survives a queue switch even when
  // this queue's views cannot name it — the view is saved for another queue,
  const isSelectedViewMissingInQueue = !!selectedViewId && !currentView;

  const isCurrentViewStale = currentView
    ? isSavedViewStale(currentView.parsedQuery, currentViewConfig.filtering)
    : false;

  const handleOnClose = () => {
    setAnchorEl(null);
  };

  const handleClearSelection = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    onClearSelection();
  };

  const openCreateDrawer = () => {
    setDrawerState({ type: 'create', filtering: currentViewConfig.filtering });
    handleOnClose();
  };

  const openEditDrawer = (view: NormalizedSavedView) => {
    setDrawerState({ type: 'edit', savedView: view });
    handleOnClose();
  };

  const handleItemClick = (params: OnMenuItemClickParams) => {
    onMenuItemClick(params);
    handleOnClose();
  };

  const systemViewButtonLabel = systemView?.isActive
    ? systemView.label
    : undefined;
  return (
    <>
      <SavedViewsConfigDrawer
        state={drawerState}
        columns={columns}
        onClose={() => setDrawerState(null)}
        activeQueueId={activeQueueId}
        codecFieldTypes={codecFieldTypes}
        onDelete={onMenuItemDelete}
      />
      <Tooltip
        title={
          isSelectedViewMissingInQueue
            ? intl.formatMessage({
                id: 'containers.savedViews.tooltips.viewMissingInQueue',
              })
            : ''
        }
        placement="top"
      >
        <span>
          <Button
            onClick={e => setAnchorEl(e.currentTarget)}
            disabled={isFetching}
            color="secondary"
            variant="outlined"
            data-cy="open-saved-views-button"
            endIcon={
              isFetching ? (
                <CircularProgress size={16} />
              ) : (
                <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                  {isCurrentViewStale && (
                    <Stack
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: borderRadius('xl'),
                        backgroundColor: 'warning.main',
                      }}
                      data-cy="stale-saved-view-indicator"
                    />
                  )}
                  {selectedViewId ? (
                    <IconButton
                      size="small"
                      onClick={handleClearSelection}
                      data-cy="clear-saved-view-action"
                    >
                      <SvgIcon>
                        <IconX />
                      </SvgIcon>
                    </IconButton>
                  ) : null}
                  <SvgIcon
                    sx={{
                      transition: t =>
                        t.transitions.create('transform', {
                          duration: t.transitions.duration.short,
                        }),
                      transform: anchorEl ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                    fontSize="medium"
                  >
                    <IconCustomMenuDown />
                  </SvgIcon>
                </Stack>
              )
            }
            {...restOfbuttonProps}
          >
            <Typography
              component="span"
              variant="inherit"
              color={isSelectedViewMissingInQueue ? 'text.disabled' : 'inherit'}
              sx={{
                textOverflow: 'inherit',
                whiteSpace: 'inherit',
                overflow: 'inherit',
              }}
            >
              {currentView?.name ??
                systemViewButtonLabel ??
                buttonLabel ??
                intl.formatMessage({
                  id: isSelectedViewMissingInQueue
                    ? 'containers.savedViews.selectedViewFallback'
                    : 'containers.savedViews.selectButton',
                })}
            </Typography>
          </Button>
        </span>
      </Tooltip>

      <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={handleOnClose}>
        <Stack sx={{ pt: 1 }} spacing={1} useFlexGap>
          <TextField
            value={search}
            onChange={e => setSearch(e.target.value)}
            size="small"
            placeholder={intl.formatMessage({
              id: 'containers.savedViews.searchPlaceholder',
            })}
            autoFocus
            sx={{ px: 2 }}
          />
          <Stack
            sx={{
              width: 300,
              minHeight: 200,
              maxHeight: 400,
              overflowY: 'auto',
              flexGrow: 1,
            }}
          >
            {systemView && isSystemViewListed ? (
              <MenuItem
                selected={systemView.isActive}
                onClick={() => {
                  systemView.onSelect();
                  handleOnClose();
                }}
              >
                <Typography>{systemView.label}</Typography>
              </MenuItem>
            ) : null}
            {normalizedSavedViews.length === 0 ? (
              !isSystemViewListed ? (
                <EmptyViewsList searchResult={search} />
              ) : null
            ) : (
              normalizedSavedViews.map(view => (
                <SavedViewMenuItem
                  key={view.id}
                  name={view.name}
                  queueCount={view.queuesFilter.length}
                  onClick={() => {
                    handleItemClick({
                      viewId: view.id.toString(),
                      filterItems: view.parsedQuery,
                    });
                  }}
                  onEdit={() => openEditDrawer(view)}
                  selected={currentView?.id === view.id}
                  missingSchemaColumns={view.missingColumns}
                />
              ))
            )}
          </Stack>
          <Divider />
          <Button
            onClick={openCreateDrawer}
            data-cy="saved-view-create-action"
            startIcon={
              <SvgIcon fontSize="medium">
                <IconCirclePlus />
              </SvgIcon>
            }
            color="secondary"
            sx={{ alignSelf: 'center' }}
          >
            {intl.formatMessage({ id: 'containers.savedViews.createButton' })}
          </Button>
        </Stack>
      </Menu>
    </>
  );
};
